"use client";

import * as React from "react";

import { useFeatureFlag } from "~/components/feature-flags/provider";
import { SizeGuideDialog } from "~/components/product/variant/SizeGuideDialog";
import { LocaleLink } from "~/i18n/provider";
import { buyingInformationGaps } from "~/lib/catalog/buying-information";
import { PRODUCT_FACT_PRESETS } from "~/lib/catalog/product-facts";
import type { PdpAttribute, PdpProduct, PdpVariant } from "~/server/api/schemas/pdp";

const KNOWN_FACT_SLUGS = new Set(PRODUCT_FACT_PRESETS.map((preset) => preset.slug));

function dimensionRows(product: PdpProduct, variant: PdpVariant | null) {
  const dimensions = [
    ["Length", variant?.lengthMm ?? product.lengthMm],
    ["Width", variant?.widthMm ?? product.widthMm],
    ["Height", variant?.heightMm ?? product.heightMm],
  ] as const;
  return dimensions.filter(([, value]) => typeof value === "number" && value > 0);
}

/** Displays authored/verified facts without deriving or guessing fit claims. */
export function ProductFacts({
  product,
  variant,
  hasSizeOption,
}: {
  product: PdpProduct;
  variant: PdpVariant | null;
  hasSizeOption: boolean;
}) {
  const guideEnabled = useFeatureFlag("pdpSizeGuide");
  const factsEnabled = useFeatureFlag("pdpProductFacts");
  const [guideOpen, setGuideOpen] = React.useState(false);
  const attrs = (product.attributes ?? []).filter(
    (attribute: PdpAttribute) => KNOWN_FACT_SLUGS.has(attribute.slug) &&
      attribute.value !== "",
  );
  const dimensions = dimensionRows(product, variant);
  const showGuide = guideEnabled && product.sizeGuideHtml.length > 0 && !hasSizeOption;
  if (product.isVirtual || product.isGiftCard || !(product.fitDetailsVisible ?? factsEnabled)) return null;
  const missing = buyingInformationGaps({ attributes: attrs, lengthMm: variant?.lengthMm ?? product.lengthMm, widthMm: variant?.widthMm ?? product.widthMm, heightMm: variant?.heightMm ?? product.heightMm });

  return (
    <section aria-labelledby="pdp-product-facts-heading" className="mt-5 rounded-[var(--radius-lg)] border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="pdp-product-facts-heading" className="text-sm font-semibold text-fg">
          Fit and product details
        </h2>
        {showGuide ? (
          <button
            type="button"
            onClick={() => setGuideOpen(true)}
            className="text-sm font-medium text-primary underline underline-offset-4 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            View size guide
          </button>
        ) : null}
      </div>
      {attrs.length > 0 ? (
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
          {attrs.map((attribute) => (
            <div key={attribute.definitionId} className="flex justify-between gap-3 border-b border-border/70 pb-1.5">
              <dt className="text-fg-muted">{attribute.name}</dt>
              <dd className="text-right font-medium text-fg">
                {String(attribute.value)}{attribute.unit ? ` ${attribute.unit}` : ""}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {dimensions.length > 0 ? (
        <div className={attrs.length > 0 ? "mt-3 border-t border-border pt-3" : "mt-3"}>
          <p className="text-xs font-medium text-fg-muted">Product dimensions</p>
          <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {dimensions.map(([label, value]) => (
              <div key={label} className="flex gap-1">
                <dt className="text-fg-muted">{label}</dt>
                <dd className="font-medium text-fg">{value} mm</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
      <div className="mt-3 text-xs text-fg-muted">
        {missing.length > 0 && <p>Not listed in structured details: {missing.join(", ")}. Check the description or ask us to confirm before buying.</p>}
        <p className="mt-1">Check that the fitting and fastening suit your item. Photos do not confirm measurements or what is included. <LocaleLink href={`/contact?product=${encodeURIComponent(product.slug)}`} className="font-medium text-primary underline">Ask about this product</LocaleLink>.</p>
      </div>
      {showGuide ? (
        <SizeGuideDialog
          open={guideOpen}
          onOpenChange={setGuideOpen}
          html={product.sizeGuideHtml}
        />
      ) : null}
    </section>
  );
}
